// Full-screen 門鈴來電卡 — 全域 WSS 層之上的「響鈴」狀態（16 §2.2 room 廣播 /
// device 03 §7.1 event frame）。
//
// 來源與既有未讀紅點同一條 onLiveEvent 串流（liveEvents.ts），但門鈴是「來電」
// 語意：不論在哪個 tab 都要全螢幕奪取注意力（不像紅點僅在非 events tab 累計）。
// 一次只呈現一張卡（單一 current）：同裝置重複響鈴 → 刷新計時;不同裝置 → 取代。
// TTL 後自動收起（門鈴響鈴窗,無使用者動作 = 略過）。
//
// 「接聽」= 導向該裝置既有 WebRTC 即時頁（/devices/:id/live）—— 不假造任何尚未
// 存在的雙向通話路徑;web 的接聽就是看即時影像（spec 一致,誠實）。

import { clusterOfEvent, type EventItem } from '../events/eventsLib';

/** 門鈴事件判定：型別 cluster = doorbell（0x8030;event_id hex 為準,退 event_type 粗分類）。 */
export function isDoorbellEvent(ev: Pick<EventItem, 'event_id' | 'event_type'>): boolean {
  return clusterOfEvent(ev).key === 'doorbell';
}

export interface Ring {
  deviceId: string;
  deviceName: string;
}

/** 門鈴響鈴窗（無動作自動收起）。native 端走 CallKit 計時;web v1 取 30s。 */
export const RING_TTL_MS = 30_000;

let current: Ring | null = null;
const subs = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

function notify(): void {
  for (const fn of subs) fn();
}

function armTimer(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => doorbellRing.dismiss(), RING_TTL_MS);
}

export const doorbellRing = {
  get(): Ring | null {
    return current;
  },
  /** 響鈴：呈現/刷新單卡。同 deviceId 視為同一串響鈴(只刷新計時,不重繪)。 */
  ring(deviceId: string, deviceName: string): void {
    if (!deviceId) return;
    const same = current && current.deviceId === deviceId;
    if (!same) {
      current = { deviceId, deviceName: deviceName || deviceId };
      notify();
    }
    armTimer();
  },
  /** 收起（接聽 / 查看 / 關閉 / TTL 到期皆走此）。 */
  dismiss(): void {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!current) return;
    current = null;
    notify();
  },
  subscribe(fn: () => void): () => void {
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  },
};

/** 測試用：重置模組狀態。 */
export function __resetDoorbellForTest(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  current = null;
  subs.clear();
}
